// The head branch's Gaussians in the eye test scene (`?eyetest=1&head=1`).
// Why opt-in, and why it leaves the wireframe toggle alone: docs/OGS_EYES.md.

import { useEffect, useRef, useSyncExternalStore } from 'react'
import { useThree } from '@react-three/fiber'
import { createNeutralBranchSystem } from '../ogs/graph/neutralBranchSystem.ts'
import { eyeTestState, eyeTestVersion, subscribeEyeTest } from './eyeTestStore.js'

export default function NeutralBranchMount({ base, onStatus }) {
  const { camera, scene, controls } = useThree()
  const systemRef = useRef(null)
  // Visibility is applied on the store's own version, so nothing re-asserts a stale value.
  useSyncExternalStore(subscribeEyeTest, eyeTestVersion)
  const { headSplats } = eyeTestState.show

  useEffect(() => {
    let disposed = false
    onStatus?.({ status: 'loading' })

    createNeutralBranchSystem({
      scene,
      camera,
      base,
      controls,
      isActive: () => !disposed,
    })
      .then((system) => {
        if (disposed) { system.dispose(); return }
        systemRef.current = system
        window.__NEUTRAL_BRANCHES__ = system
        // The sync effect below only fires on a CHANGE: a mount with the toggle off must not render.
        system.setVisible(eyeTestState.show.headSplats)
        onStatus?.({ status: 'ready', system })
      })
      .catch((error) => {
        console.error('[eyetest] head branch failed to mount', error)
        onStatus?.({ status: 'error', error: error?.message || String(error) })
      })

    return () => {
      disposed = true
      const mounted = systemRef.current
      systemRef.current = null
      if (window.__NEUTRAL_BRANCHES__ === mounted) delete window.__NEUTRAL_BRANCHES__
      mounted?.dispose()
    }
  }, [base, camera, controls, scene, onStatus])

  useEffect(() => { systemRef.current?.setVisible(headSplats) }, [headSplats])

  return null
}
